/**
 * Skills Intelligence™ — Layer 2 of the HDI stack, in the browser.
 *
 * 40 evidence items grouped by domain. Nothing here is a self-rating: every
 * item asks what you have done, used or shipped. All scoring happens in the
 * engine, server-side; this page renders what comes back and always says why.
 */

import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Loader2, RefreshCw, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useSkillsIntelligence, type StoredEvidence } from "@/hooks/useSkillsIntelligence";
import { EVIDENCE_ITEMS } from "@wcc360/engine/skills-engine";
import type { EvidenceItem, SkillDomain } from "@wcc360/engine/skills-engine";
import { FutureProbabilityPanel } from "@/components/skills/FutureProbabilityPanel";
import { SkillsGraphPanel } from "@/components/skills/SkillsGraphPanel";
import OnetAttribution from "@/components/OnetAttribution";

const DOMAIN_LABELS: Record<SkillDomain, string> = {
  digital: "Digital",
  data: "Data",
  ai: "AI",
  engineering: "Making & engineering",
  design: "Design",
  communication: "Communication",
  leadership: "Leading people",
  enterprise: "Enterprise & money",
  care: "Care & service",
  learning: "Learning how to learn",
};

const DOMAIN_ORDER = Object.keys(DOMAIN_LABELS) as SkillDomain[];

export default function SkillsIntelligence() {
  const { toast } = useToast();
  const {
    loading, saving, error, coverage, skills, indicators, evidence,
    saveEvidence, recompute,
  } = useSkillsIntelligence();

  // Local answer state, keyed by item. Hydrated from stored evidence.
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (loading || hydrated) return;
    const next: Record<string, string[]> = {};
    for (const row of evidence) next[row.item_id] = row.option_ids ?? [];
    setAnswers(next);
    setHydrated(true);
  }, [loading, hydrated, evidence]);

  const grouped = useMemo(() => {
    const map = new Map<SkillDomain, EvidenceItem[]>();
    for (const item of EVIDENCE_ITEMS) {
      const list = map.get(item.domain) ?? [];
      list.push(item);
      map.set(item.domain, list);
    }
    return DOMAIN_ORDER.filter((d) => map.has(d)).map((d) => ({ domain: d, items: map.get(d)! }));
  }, []);

  const answeredCount = Object.values(answers).filter((v) => v.length > 0).length;
  const localCoverage = Math.round((answeredCount / EVIDENCE_ITEMS.length) * 100);

  function toggle(item: EvidenceItem, optionId: string) {
    setAnswers((prev) => {
      const current = prev[item.item_id] ?? [];
      if (!item.multi) return { ...prev, [item.item_id]: current[0] === optionId ? [] : [optionId] };
      return {
        ...prev,
        [item.item_id]: current.includes(optionId)
          ? current.filter((o) => o !== optionId)
          : [...current, optionId],
      };
    });
  }

  async function onSave() {
    const records: StoredEvidence[] = Object.entries(answers)
      .filter(([, opts]) => opts.length > 0)
      .map(([item_id, option_ids]) => ({ item_id, option_ids, source: "questionnaire" as const }));

    if (!records.length) {
      toast({ title: "Nothing to save yet", description: "Tick anything you've actually done — even one thing moves it." });
      return;
    }
    const ok = await saveEvidence(records);
    toast({
      title: ok ? "Saved. Your indicators just moved." : "Couldn't save",
      description: ok
        ? "Everything below is recalculated from what you've done."
        : error ?? "Please try again in a moment.",
      variant: ok ? undefined : "destructive",
    });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Skills Intelligence™ | WeKIT Career Clarity 360</title>
        <meta
          name="description"
          content="Map what you've actually done to open-standard skills (ESCO) and see your five living Future Probability indicators update as you build."
        />
      </Helmet>

      <header className="border-b border-border/60">
        <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="text-[11px] font-display uppercase tracking-[0.35em] text-primary/80">
            Layer 2 · Skills Intelligence™
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-5xl font-bold text-balance max-w-3xl">
            Not what you think you're good at. What you've actually done.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            Forty short questions about real things — built, used, shipped, fixed. We map them to
            open, public skill standards, then your Future Probability indicators move on their own.
          </p>

          <div className="mt-8 max-w-md">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">Evidence gathered</span>
              <span className="font-display text-lg">{localCoverage}%</span>
            </div>
            <Progress value={localCoverage} className="mt-2 h-2" aria-label="Evidence gathered" />
            <p className="mt-2 text-xs text-muted-foreground">
              {answeredCount} of {EVIDENCE_ITEMS.length} answered. Stop any time — everything saves.
            </p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-14">
        <FutureProbabilityPanel
          indicators={indicators}
          loading={loading}
          coverage={coverage}
          onRefresh={recompute}
          refreshing={saving}
        />

        <SkillsGraphPanel skills={skills} loading={loading} />

        <OnetAttribution variant="card" />

        <section aria-labelledby="evidence-heading" className="space-y-10">
          <div>
            <h2 id="evidence-heading" className="font-display text-2xl sm:text-3xl font-bold">
              Your evidence
            </h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Tick everything that's true. Half-finished counts. Nothing here is a test.
            </p>
          </div>

          {grouped.map(({ domain, items }) => (
            <div key={domain} className="space-y-4">
              <div className="flex items-center gap-3">
                <h3 className="font-display text-lg font-semibold">{DOMAIN_LABELS[domain]}</h3>
                <Badge variant="secondary" className="text-[10px]">{items.length} questions</Badge>
              </div>

              {items.map((item, i) => (
                <motion.fieldset
                  key={item.item_id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.2) }}
                  className="rounded-3xl border border-border bg-card/60 p-5 sm:p-6"
                >
                  <legend className="font-medium text-base sm:text-lg">{item.prompt}</legend>
                  {item.helper && (
                    <p className="mt-1 text-sm text-muted-foreground">{item.helper}</p>
                  )}

                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {item.options.map((opt) => {
                      const selected = (answers[item.item_id] ?? []).includes(opt.option_id);
                      return (
                        <button
                          key={opt.option_id}
                          type="button"
                          role={item.multi ? "checkbox" : "radio"}
                          aria-checked={selected}
                          onClick={() => toggle(item, opt.option_id)}
                          className={`flex items-start gap-3 text-left rounded-2xl border p-4 min-h-[56px]
                            transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                            ${selected
                              ? "border-primary bg-primary/10"
                              : "border-border bg-card hover:border-primary/60 hover:bg-primary/5"}`}
                        >
                          <span
                            aria-hidden
                            className={`mt-0.5 grid place-items-center h-5 w-5 shrink-0 rounded-md border
                              ${selected ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}
                          >
                            {selected && <Check className="h-3.5 w-3.5" />}
                          </span>
                          <span className="text-sm leading-snug">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.fieldset>
              ))}
            </div>
          ))}
        </section>
      </main>

      {/* Sticky save — you never lose a session's work. */}
      <div className="sticky bottom-0 z-20 border-t border-border/60 bg-background/90 backdrop-blur">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3 justify-between">
          <p className="text-xs text-muted-foreground">
            {answeredCount} answered · saved to your Career Passport
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => void recompute()} disabled={saving}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${saving ? "animate-spin" : ""}`} />
              Refresh indicators
            </Button>
            <Button onClick={() => void onSave()} disabled={saving} className="min-h-[48px]">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Save & recalculate
            </Button>
            <Button asChild variant="outline" className="min-h-[48px] hidden sm:inline-flex">
              <Link to="/vision-board">
                Vision Board <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
